import { useEffect, useRef, useState } from 'react';
import { sendLocations, useActivitySettings } from '@/api/activity';
import type { Schemas } from '@/api/client';
import { useRunningTimer } from '@/api/timeEntries';
import { useCurrentWorkspace } from './WorkspaceContext';

const MAX_BUFFER = 500;

/**
 * Standorterfassung bei laufendem Timer (ACT-01, PWA-07): nur wenn der Workspace sie einschaltet,
 * die Person zugestimmt hat und der Browser Standortzugriff gewährt. Punkte werden gepuffert und im
 * eingestellten Intervall gesendet. Im Hintergrund drosseln Browser die Geolocation – ein Hinweis
 * steht im Profil.
 */
export function useLocationTracking(): { active: boolean; error: string | null } {
  const ctx = useCurrentWorkspace();
  const allowed = ctx.hasFeature('ACTIVITY') && ctx.hasFeature('GPS');
  const { data: settings } = useActivitySettings(ctx.workspaceId, allowed);
  const running = useRunningTimer(ctx.workspaceId);
  const enabled = allowed && !!settings?.gpsEnabled && !!settings.consent && !!running.data && typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const interval = (settings?.intervalSeconds ?? 300) * 1000;
  const buffer = useRef<Schemas['Point'][]>([]);
  const lastSample = useRef(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let sentOnce = false;
    function sample(pos: GeolocationPosition) {
      setError(null);
      // Empfangszeit statt Positions-Zeitstempel: zwischengespeicherte Positionen sind sonst „älter“ als der Timer
      const now = Date.now();
      if (now - lastSample.current < interval / 2) return;
      lastSample.current = now;
      buffer.current.push({
        recordedAt: new Date(now).toISOString(),
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });
      if (buffer.current.length > MAX_BUFFER) buffer.current.splice(0, buffer.current.length - MAX_BUFFER);
      // erster Punkt nach dem Start sofort senden, danach im Intervall
      if (!sentOnce) {
        sentOnce = true;
        flush();
      }
    }
    function flush() {
      if (buffer.current.length === 0) return;
      const batch = buffer.current.splice(0, buffer.current.length);
      sendLocations(ctx.workspaceId, batch).catch(() => {
        // offline o. ä.: zurück in den Puffer und beim nächsten Intervall erneut senden
        buffer.current.unshift(...batch);
      });
    }
    const onError = (err: GeolocationPositionError) => setError(err.message);
    const options = { enableHighAccuracy: true, maximumAge: 30_000, timeout: 60_000 };
    // watchPosition meldet nur Änderungen – zusätzlich im Intervall aktiv abfragen
    const watch = navigator.geolocation.watchPosition(sample, onError, options);
    const timer = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(sample, onError, options);
      flush();
    }, interval);
    return () => {
      navigator.geolocation.clearWatch(watch);
      window.clearInterval(timer);
      flush();
    };
  }, [enabled, interval, ctx.workspaceId]);

  return { active: enabled, error };
}
